import { useCallback, useMemo, useState } from "react";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";

import type { CivilDate } from "@/components/ui/date-time";
import type { ScheduleCalendarRange } from "@/features/wonda/components/primitives";
import { Button } from "@/components/ui/Button";
import {
  browserTimeZone,
  civilDateKey,
  nowInZone,
} from "@/components/ui/date-time";
import {
  CalendarScreen,
  ScheduleFormDialog,
} from "@/features/wonda/components/calendar";
import { CliFooter } from "@/landing/components/CliFooter";
import { CliNav } from "@/landing/components/CliNav";

// The calendar: what the account's twins are about to do, and what they already
// did, plus the one creation affordance in the whole surface.
//
// The visible window drives `calendar.summary` (per-day aggregates) and the
// selected day drives `calendar.day` (real rows). Both carry the viewer's IANA
// zone, because a fire at 09:00 Paris must land on the Paris day even when the
// row is stored in UTC.

export const Route = createFileRoute("/_authenticated/(wonda)/_wonda/calendar")(
  {
    component: CalendarRoute,
  },
);

function CalendarRoute() {
  const api = useRouteContext({
    from: "/_authenticated",
    select: (ctx) => ctx.api,
  });
  const utils = api.useUtils();

  const timeZone = useMemo(() => browserTimeZone(), []);
  const today = useMemo(() => nowInZone(timeZone), [timeZone]);
  const [selectedDay, setSelectedDay] = useState<CivilDate>(today);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Stable identity: ScheduleCalendar re-runs its range effect whenever this
  // changes, so an inline arrow here would loop forever.
  const onRangeChange = useCallback((next: ScheduleCalendarRange) => {
    const from = civilDateKey(next.from);
    const to = civilDateKey(next.to);
    setRange((current) =>
      current?.from === from && current.to === to ? current : { from, to },
    );
  }, []);

  const { data: summary = null, isLoading: isLoadingSummary } =
    api.calendar.summary.useQuery(
      { from: range?.from ?? "", to: range?.to ?? "", timeZone },
      { enabled: range != null, staleTime: 30_000, retry: false },
    );

  const { data: day = null, isLoading: isLoadingDay } =
    api.calendar.day.useQuery(
      { date: civilDateKey(selectedDay), timeZone, limit: 200 },
      { staleTime: 15_000, retry: false },
    );

  const { data: twins = [] } = api.twin.listSessions.useQuery(undefined, {
    staleTime: 60_000,
    retry: false,
  });
  const personas = useMemo(() => twins.map((twin) => twin.persona), [twins]);

  const createSchedule = api.twin.createSchedule.useMutation({
    onSuccess: () => {
      setIsFormOpen(false);
      setFormError(null);
      void utils.calendar.summary.invalidate();
      void utils.calendar.day.invalidate();
      toast.success("Schedule created");
    },
    onError: (error) =>
      setFormError(error.message ?? "The schedule could not be created"),
  });

  return (
    <div className="flex min-h-[100dvh] flex-col">
      <CliNav />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">
        <CalendarScreen
          timeZone={timeZone}
          summary={summary}
          day={day}
          selectedDay={selectedDay}
          onSelectedDayChange={setSelectedDay}
          onRangeChange={onRangeChange}
          isLoadingSummary={isLoadingSummary}
          isLoadingDay={isLoadingDay}
          headerActions={
            personas.length > 0 ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setFormError(null);
                  setIsFormOpen(true);
                }}
              >
                <CalendarPlus className="size-3.5" strokeWidth={1.5} />
                New recurring action
              </Button>
            ) : null
          }
        />

        <ScheduleFormDialog
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          personas={personas}
          isBusy={createSchedule.isPending}
          error={formError}
          onCreate={(draft) =>
            createSchedule.mutate({
              persona: draft.persona,
              name: draft.name,
              taskKind: "engage",
              cron: draft.cadence.cron,
              timezone: draft.cadence.timeZone,
              commandArgv: draft.commandArgv,
              jitterMinSeconds: draft.cadence.jitterMinSeconds,
              jitterMaxSeconds: draft.cadence.jitterMaxSeconds,
            })
          }
        />
      </main>
      <CliFooter />
    </div>
  );
}
